import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnDestroy,
  Output,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';

/**
 * Modale de visualisation d'une photo : réglage de la luminosité, zoom (slider +
 * molette, centré sur le curseur) et déplacement (glisser) une fois zoomé.
 * Piloté par l'entrée `picture` (URL, ou null = fermée) ; émet `closed` à la
 * fermeture. Logique reprise de i-carousel (i-viewer), qu'on n'utilise plus.
 * ViewEncapsulation.None : le contenu est projeté dans un overlay rattaché au
 * <body> par p-dialog, les styles doivent donc être globaux.
 */
@Component({
  selector: 'app-picture-viewer',
  standalone: false,
  templateUrl: './picture-viewer.component.html',
  styleUrl: './picture-viewer.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class PictureViewerComponent implements OnDestroy {
  @Input() public picture: string | null = null;
  @Output() public closed = new EventEmitter<void>();

  @ViewChild('zoomImage') private zoomImage?: ElementRef<HTMLImageElement>;

  private renderer: Renderer2 = inject(Renderer2);
  private ngZone: NgZone = inject(NgZone);

  public currentBrightness = 100;
  public currentZoom = 100;
  public readonly minZoom = 100;
  public readonly maxZoom = 1000;
  public readonly zoomStep = 10;
  public isDragging = false;
  private translateX = 0;
  private translateY = 0;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragStartTranslateX = 0;
  private dragStartTranslateY = 0;
  private removeMouseMoveListener?: () => void;
  private removeMouseUpListener?: () => void;

  public ngOnDestroy(): void {
    this.stopDrag();
  }

  public close(): void {
    this.stopDrag();
    this.currentBrightness = 100;
    this.currentZoom = 100;
    this.translateX = 0;
    this.translateY = 0;
    this.closed.emit();
  }

  public updateBrightness(value: number): void {
    this.currentBrightness = value;
    const img = this.zoomImage?.nativeElement;
    if (img) {
      img.style.filter = `brightness(${this.currentBrightness}%)`;
    }
  }

  public updateZoom(value: number): void {
    this.zoomTo(value, 0, 0);
  }

  public onImageWheel(event: WheelEvent): void {
    event.preventDefault();
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const anchorX = event.clientX - (rect.left + rect.width / 2);
    const anchorY = event.clientY - (rect.top + rect.height / 2);
    const delta = event.deltaY < 0 ? this.zoomStep : -this.zoomStep;
    this.zoomTo(this.currentZoom + delta, anchorX, anchorY);
  }

  public onImageMouseDown(event: MouseEvent): void {
    if (this.currentZoom <= this.minZoom) {
      return;
    }
    event.preventDefault();
    this.isDragging = true;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.dragStartTranslateX = this.translateX;
    this.dragStartTranslateY = this.translateY;
    this.ngZone.runOutsideAngular(() => {
      this.removeMouseMoveListener = this.renderer.listen(
        'document',
        'mousemove',
        (e: MouseEvent) => this.onDrag(e),
      );
      this.removeMouseUpListener = this.renderer.listen(
        'document',
        'mouseup',
        () => this.stopDrag(),
      );
    });
  }

  private zoomTo(value: number, anchorX: number, anchorY: number): void {
    const newZoom = Math.min(this.maxZoom, Math.max(this.minZoom, value));
    const ratio = newZoom / this.currentZoom;
    this.translateX = anchorX - ratio * (anchorX - this.translateX);
    this.translateY = anchorY - ratio * (anchorY - this.translateY);
    this.currentZoom = newZoom;
    this.applyTransform();
  }

  private onDrag(event: MouseEvent): void {
    this.translateX =
      this.dragStartTranslateX + (event.clientX - this.dragStartX);
    this.translateY =
      this.dragStartTranslateY + (event.clientY - this.dragStartY);
    this.applyTransform();
  }

  private stopDrag(): void {
    this.removeMouseMoveListener?.();
    this.removeMouseUpListener?.();
    this.removeMouseMoveListener = undefined;
    this.removeMouseUpListener = undefined;
    if (this.isDragging) {
      this.ngZone.run(() => (this.isDragging = false));
    }
  }

  private applyTransform(): void {
    const img = this.zoomImage?.nativeElement;
    if (!img) {
      return;
    }
    this.clampTranslate(img);
    img.style.transform = `translate(${this.translateX}px, ${this.translateY}px) scale(${
      this.currentZoom / 100
    })`;
  }

  private clampTranslate(img: HTMLElement): void {
    const scale = this.currentZoom / 100;
    const maxX = Math.max(0, (img.clientWidth * (scale - 1)) / 2);
    const maxY = Math.max(0, (img.clientHeight * (scale - 1)) / 2);
    this.translateX = Math.min(maxX, Math.max(-maxX, this.translateX));
    this.translateY = Math.min(maxY, Math.max(-maxY, this.translateY));
  }
}
